import React, { useEffect, useState } from 'react';
import Barcode from './Barcode';
import { FiInfo } from 'react-icons/fi';
import { applyPadKey, sanitizeQuantityInput } from '../utils/quantityInputParser';
import { isScaleWeightMetricUnit } from '../utils/scalePresentation';

const truncateDescription = (value, maxLength = 80) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

const Modal = ({
  isOpen,
  onClose,
  variety,
  quantities,
  closeModalVariety,
  closeModalProduct,
  handleQuantityChange,
  setShowDiscountModal,
  discounts,
  edit,
  varietyPrice,
  canDiscount = true,
  resolveDisplayPrice,
  onConfirmQuantity,
  initialQuantity = null,
  confirmLabel = 'Confirmar',
  updateOnChange = false,
}) => {
  const [rawQuantity, setRawQuantity] = useState('0');
  const isMissingPrice = varietyPrice?.price === null || varietyPrice?.price === undefined;
  const priceIndex = Array.isArray(variety?.VarietyPrices)
    ? variety.VarietyPrices.findIndex((entry) => (
      varietyPrice?.id != null ? entry?.id === varietyPrice.id : entry === varietyPrice
    ))
    : -1;
  const isKgUnit = isScaleWeightMetricUnit(varietyPrice?.metricUnit);
  const usesManualConfirm = typeof onConfirmQuantity === 'function';
  const shouldRenderConfirmAction = usesManualConfirm && !updateOnChange;
  const unitDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
  const displayBase = variety && varietyPrice && typeof resolveDisplayPrice === 'function'
    ? resolveDisplayPrice(variety, varietyPrice, 0)
    : null;
  const displayDiscounted = variety && varietyPrice && typeof resolveDisplayPrice === 'function'
    ? resolveDisplayPrice(variety, varietyPrice, unitDiscount)
    : null;
  const shownBasePrice = Number.isFinite(Number(displayBase?.price))
    ? Number(displayBase.price)
    : Number(varietyPrice?.price || 0);
  const shownDiscountedPrice = Number.isFinite(Number(displayDiscounted?.price))
    ? Number(displayDiscounted.price)
    : (Number(varietyPrice?.price || 0) - unitDiscount);
  const taxFreeApplied = Boolean(displayBase?.taxFreeApplied || displayDiscounted?.taxFreeApplied);
  const formattedShownSubtotal = Math.floor(shownDiscountedPrice * parseFloat(rawQuantity || 0)).toLocaleString('De-de');
  const parsedRawQuantity = Number.parseFloat(rawQuantity || 0) || 0;

  useEffect(() => {
    if (!isOpen || !usesManualConfirm || priceIndex < 0 || !variety?.id) return;
    const initialValue = initialQuantity === null || initialQuantity === undefined
      ? '0'
      : String(initialQuantity).replace(',', '.');
    setRawQuantity(sanitizeQuantityInput(initialValue, isKgUnit));
  }, [initialQuantity, isKgUnit, isOpen, priceIndex, usesManualConfirm, variety?.id, varietyPrice?.id]);

  useEffect(() => {
    if (!isOpen || usesManualConfirm || priceIndex < 0 || !variety?.id) return;
    const currentQuantity = quantities[variety.id]?.[priceIndex];
    const currentValue = currentQuantity === null || currentQuantity === undefined
      ? '0'
      : String(currentQuantity).replace(',', '.');
    setRawQuantity(sanitizeQuantityInput(currentValue, isKgUnit));
  }, [isKgUnit, isOpen, priceIndex, quantities, usesManualConfirm, variety?.id, varietyPrice?.id]);

  if (!isOpen || !variety || !varietyPrice || isMissingPrice || priceIndex < 0) return null;

  const handleInputChange = (valueOrEvent) => {
    const nextValue = sanitizeQuantityInput(valueOrEvent, isKgUnit);
    setRawQuantity(nextValue);
    if (nextValue.endsWith('.')) {
      return;
    }
    if (usesManualConfirm && !updateOnChange) return;
    handleQuantityChange(variety.id, priceIndex, nextValue);
  };

  const handlePadInput = (input) => {
    const nextValue = applyPadKey(rawQuantity, input, isKgUnit);
    setRawQuantity(nextValue);
    if (nextValue.endsWith('.')) {
      return;
    }
    if (usesManualConfirm && !updateOnChange) return;
    handleQuantityChange(variety.id, priceIndex, nextValue);
  };

  const handleButtonChange = (increment) => {
    let numericValue = parseFloat(rawQuantity) || 0;
    numericValue += increment;
    const nextValue = isKgUnit
      ? (Math.max(numericValue, 0).toFixed(3).replace(/\.?0+$/, '') || '0')
      : String(Math.max(Math.trunc(numericValue), 0));
    setRawQuantity(nextValue);
    if (!usesManualConfirm || updateOnChange) {
      handleQuantityChange(variety.id, priceIndex, nextValue);
    }
  };

  const handleConfirm = async () => {
    if (!usesManualConfirm) return;
    if (!Number.isFinite(parsedRawQuantity) || parsedRawQuantity <= 0) return;
    const shouldClose = await onConfirmQuantity(parsedRawQuantity);
    if (shouldClose === false) return;
    closeEverything();
  };

  const closeEverything = () => {
    onClose();
    closeModalVariety();
    closeModalProduct();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto pt-6"
      onClick={onClose}
    >
      <div
        className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full mx-4 relative max-h-[88vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
        >
          X
        </button>

        <div className="flex justify-center">
          <div className="relative w-[72px] h-[72px] sm:w-[90px] sm:h-[90px] mb-3 mt-1 sm:mb-4 sm:mt-2">
            <img
              src={varietyPrice.priceImage || variety.varietyImage || 'https://placehold.co/200'}
              alt={variety.varietyName}
              className="absolute inset-0 w-full h-full object-cover rounded"
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-1">
          <h2 className="text-lg font-bold truncate mr-2">{variety.varietyName}</h2>
          <span className="text-sm text-gray-700">{varietyPrice.metricUnit || ''}</span>
        </div>

        <p className="text-sm text-gray-700 mb-4 break-all leading-snug">
          {truncateDescription(varietyPrice.description)}
        </p>

        {varietyPrice.barcode ? (
          <Barcode value={varietyPrice.barcode} className="mb-4" />
        ) : (
          <p className="mb-4 text-xs text-gray-500 text-center">
            Sin codigo de barras
          </p>
        )}

        {varietyPrice.isSoldOut && <p className="text-lg font-bold text-red-600">AGOTADO</p>}

        <div className="flex justify-between items-center mb-2">
          <p className="text-md font-bold">
            {unitDiscount ? (
              <>
                <span className="line-through text-gray-500 mr-2">${shownBasePrice.toLocaleString('De-de')}</span>
                <span>${shownDiscountedPrice.toLocaleString('De-de')}</span>
              </>
            ) : (
              `$${shownBasePrice.toLocaleString('De-de')}`
            )}
            {taxFreeApplied && (
              <span
                className="inline-flex items-center ml-2 text-blue-600 align-middle"
                title="Precio ajustado sin impuestos excluidos."
                aria-label="Precio ajustado sin impuestos excluidos."
              >
                <FiInfo size={14} />
              </span>
            )}
          </p>
          <p className="text-md font-semibold">Subtotal: ${formattedShownSubtotal}</p>
        </div>

        {edit && (
          <>
            <div className="flex items-center mt-2">
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  handleButtonChange(-1);
                }}
                className="w-10 h-10 bg-red-500 text-white rounded-l hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={usesManualConfirm ? parsedRawQuantity <= 0 : Number.parseFloat(quantities[variety.id]?.[priceIndex] || 0) <= 0}
              >-</button>
              <input
                type="text"
                inputMode={isKgUnit ? 'decimal' : 'numeric'}
                value={rawQuantity}
                onChange={handleInputChange}
                className="text-center text-xl p-1 border-t border-b border-gray-300"
                style={{ width: 'calc(100% - 96px)' }}
              />
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  handleButtonChange(1);
                }}
                className="w-10 h-10 bg-green-500 text-white rounded-r hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
              >+</button>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, '<-', 'C'].map((key, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePadInput(key)}
                  className="py-3 bg-gray-100 text-xl rounded hover:bg-gray-200"
                >
                  {key}
                </button>
              ))}

              {shouldRenderConfirmAction ? (
                <button
                  onClick={handleConfirm}
                  className={`col-span-1 py-3 text-white text-md rounded ${
                    parsedRawQuantity === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                  disabled={parsedRawQuantity === 0}
                >
                  {confirmLabel}
                </button>
              ) : canDiscount && (
                <button
                  onClick={() => { setShowDiscountModal(true); }}
                  className={`col-span-1 py-3 text-white text-md rounded ${
                    parsedRawQuantity === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                  disabled={parsedRawQuantity === 0}
                >
                  + Descuento
                </button>
              )}
              <button
                onClick={closeEverything}
                className="col-span-1 py-3 bg-blue-500 text-white text-lg rounded hover:bg-blue-600"
              >
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;
